import React, { useContext, useState } from "react";
import WebtopLayoutContext from "../layout/WebtopLayoutContext";
import {
  STICKY_COLORS,
  NOTE_MIN_W,
  NOTE_MIN_H,
  updateNote,
} from "../layout/layoutModel";

/**
 * StickyNote — a single draggable/resizable/editable note on the desktop.
 * All changes go through updateLayout() → debounced backend autosave (Phase 7).
 */
const StickyNote = ({ note }) => {
  const { layout, updateLayout } = useContext(WebtopLayoutContext);
  const [transient, setTransient] = useState(null); // {x,y,w,h} during drag/resize

  const commit = (patch) =>
    updateLayout({ stickyNotes: updateNote(layout, note.id, patch).stickyNotes });

  const x = transient?.x ?? note.x;
  const y = transient?.y ?? note.y;
  const w = transient?.w ?? note.w;
  const h = transient?.h ?? note.h;

  // Drag via the header.
  const onHeaderMouseDown = (e) => {
    if (e.button !== 0) return;
    const sx = e.clientX;
    const sy = e.clientY;
    const ox = note.x;
    const oy = note.y;
    const onMove = (ev) =>
      setTransient({ x: Math.max(0, ox + ev.clientX - sx), y: Math.max(0, oy + ev.clientY - sy) });
    const onUp = (ev) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      commit({ x: Math.max(0, ox + ev.clientX - sx), y: Math.max(0, oy + ev.clientY - sy) });
      setTransient(null);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Resize from the bottom-right corner.
  const onResizeMouseDown = (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const sx = e.clientX;
    const sy = e.clientY;
    const ow = note.w;
    const oh = note.h;
    const onMove = (ev) =>
      setTransient({
        w: Math.max(NOTE_MIN_W, ow + ev.clientX - sx),
        h: Math.max(NOTE_MIN_H, oh + ev.clientY - sy),
      });
    const onUp = (ev) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      commit({
        w: Math.max(NOTE_MIN_W, ow + ev.clientX - sx),
        h: Math.max(NOTE_MIN_H, oh + ev.clientY - sy),
      });
      setTransient(null);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <div
      className="webtop-sticky"
      style={{ left: x, top: y, width: w, height: h, background: note.color }}
      role="note"
    >
      <div className="webtop-sticky__head" onMouseDown={onHeaderMouseDown}>
        <div className="webtop-sticky__colors" onMouseDown={(e) => e.stopPropagation()}>
          {STICKY_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`webtop-sticky__color${c === note.color ? " webtop-sticky__color--sel" : ""}`}
              style={{ background: c }}
              aria-label={`Color ${c}`}
              onClick={() => commit({ color: c })}
            />
          ))}
        </div>
        <button
          type="button"
          className="webtop-sticky__close"
          aria-label="Close note"
          title="Close (the note is kept in Sticky Notes)"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => commit({ open: false })}
        >
          ✕
        </button>
      </div>

      <textarea
        className="webtop-sticky__text"
        value={note.text}
        placeholder="Note…"
        aria-label="Sticky note text"
        onChange={(e) => commit({ text: e.target.value })}
      />

      <div className="webtop-sticky__resize" onMouseDown={onResizeMouseDown} />
    </div>
  );
};

export default StickyNote;
